import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Counter } from '@/types/api'
import { formatDateTime } from '@/utils/format'
import type { CombinedColumnDef } from '@/api/dowload-excel.api'

interface BuildColumnsOptions {
  pageIndex: number
  pageSize: number
  showUnitColumn: boolean
  canView?: boolean
  canUpdate?: boolean
  canDelete?: boolean
  onViewDetail?: (item: Counter) => void
  onUpdate?: (item: Counter) => void
  onDelete?: (item: Counter) => void
  onToggleStatus?: (item: Counter) => void
}

export function buildColumns({
  pageIndex,
  pageSize,
  showUnitColumn,
  canView = true,
  canUpdate = true,
  canDelete = true,
  onViewDetail,
  onUpdate,
  onDelete,
  onToggleStatus,
}: BuildColumnsOptions): CombinedColumnDef<Counter>[] {
  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      align: 'center',
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
    },
    {
      accessorKey: 'deskNumber',
      header: 'SỐ QUẦY',
      align: 'left',
      cell: ({ row }) => String(row.original.deskNumber).padStart(2, '0'),
    },
    {
      accessorKey: 'name',
      header: 'TÊN QUẦY',
      align: 'left',
      cell: ({ row }) => (
        <button
          type="button"
          className="cursor-pointer text-[#CE7A58] hover:text-[#b8694a]"
          onClick={() => onViewDetail?.(row.original)}
        >
          {row.original.name}
        </button>
      ),
    },
    ...(showUnitColumn
      ? [
          {
            accessorKey: 'unitName',
            header: 'ĐƠN VỊ',
            align: 'left',
            cell: ({ getValue }) => (getValue() as string) || '-',
          } satisfies CombinedColumnDef<Counter>,
        ]
      : []),
    {
      accessorKey: 'diagramName',
      header: 'SƠ ĐỒ',
      align: 'left',
      cell: ({ getValue }) => getValue() as string,
    },
    {
      accessorKey: 'updatedAt',
      header: 'NGÀY CẬP NHẬT',
      size: 130,
      align: 'center',
      cell: ({ getValue }) => formatDateTime(getValue()),
      exportFormatter: (v) => formatDateTime(v),
    },
    {
      accessorKey: 'status',
      header: 'TRẠNG THÁI',
      size: 110,
      align: 'center',
      cell: ({ row }) => {
        const item = row.original
        return (
          <div className="flex justify-center">
            <Switch
              checked={item.status === 1}
              disabled={!canUpdate}
              onCheckedChange={() => {
                if (!canUpdate) return
                onToggleStatus?.(item)
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'actions',
      size: 55,
      exportable: false,
      meta: { fixed: 'right' },
      align: 'center',
      cell: ({ row }) => (
        <div className="flex justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {canUpdate && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onUpdate?.(row.original)}
                >
                  Cập nhật
                </DropdownMenuItem>
              )}
              {canView && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onViewDetail?.(row.original)}
                >
                  Xem chi tiết
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  onClick={() => onDelete?.(row.original)}
                >
                  Xóa
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ]
}
